
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getEmailLogs } from "@/utils/emailUtils";
import { format, parseISO } from "date-fns";
import { el } from "date-fns/locale";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface PartnerEmailMetricsProps {
  partnerId: string;
  partnerEmail?: string;
  leads?: Array<{ id: string }>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const PartnerEmailMetrics: React.FC<PartnerEmailMetricsProps> = ({ partnerId, partnerEmail, leads = [] }) => {
  const [emailLogs, setEmailLogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<{
    totalSent: number;
    totalOpened: number;
    totalClicked: number;
    openRate: number;
    clickRate: number;
    eventCounts: {name: string, value: number}[];
  }>({
    totalSent: 0,
    totalOpened: 0,
    totalClicked: 0,
    openRate: 0,
    clickRate: 0,
    eventCounts: [],
  });

  useEffect(() => {
    const fetchEmailLogs = async () => {
      try {
        setIsLoading(true);
        // Server-side filter by partnerId — much faster than loading all logs
        const logs = await getEmailLogs(partnerId);

        const relevantLogs = [...logs].sort((a, b) => {
          const aTime = new Date(a.timestamp || a.created_at || 0).getTime();
          const bTime = new Date(b.timestamp || b.created_at || 0).getTime();
          return bTime - aTime;
        });

        setEmailLogs(relevantLogs);
        
        // Calculate metrics
        const sentEmails = relevantLogs.filter(log => log.event === "sent");
        const openedEmails = relevantLogs.filter(log => log.event === "open");
        const clickedEmails = relevantLogs.filter(log => log.event.startsWith("click-"));
        
        // Get unique tracking IDs for accurate counts
        const uniqueSentIds = new Set(sentEmails.map(log => log.trackingId));
        const uniqueOpenedIds = new Set(openedEmails.map(log => log.trackingId));
        const uniqueClickedIds = new Set(clickedEmails.map(log => log.trackingId));
        
        // Count different click types
        const clickTypes = clickedEmails.reduce((acc: {[key: string]: number}, log) => {
          const type = log.event.replace('click-', '');
          if (!acc[type]) acc[type] = 0;
          acc[type]++;
          return acc;
        }, {});
        
        const eventCountsArray = Object.entries(clickTypes).map(([name, value]) => ({
          name: name === 'view' ? 'Προβολή στοιχείων' :
                name === 'status-pending' ? 'Σε εκκρεμότητα' :
                name === 'status-completed' ? 'Ολοκληρώθηκε' :
                name === 'status-info' ? 'Πληροφορίες' : name,
          value: value as number
        }));
        
        // Add open events if any
        if (uniqueOpenedIds.size > 0) {
          eventCountsArray.unshift({
            name: 'Άνοιγμα email',
            value: uniqueOpenedIds.size
          });
        }
        
        setMetrics({
          totalSent: uniqueSentIds.size,
          totalOpened: uniqueOpenedIds.size,
          totalClicked: uniqueClickedIds.size,
          openRate: uniqueSentIds.size > 0 ? Math.round((uniqueOpenedIds.size / uniqueSentIds.size) * 100) : 0,
          clickRate: uniqueOpenedIds.size > 0 ? Math.round((uniqueClickedIds.size / uniqueOpenedIds.size) * 100) : 0,
          eventCounts: eventCountsArray,
        });
        
      } catch (error) {
        console.error("Error fetching email logs:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (partnerId) {
      fetchEmailLogs();
    }
  }, [partnerId, partnerEmail, leads]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{metrics.totalSent}</div>
            <p className="text-sm text-muted-foreground">Απεσταλμένα Emails</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{metrics.openRate}%</div>
            <p className="text-sm text-muted-foreground">Ποσοστό Αναγνώσεων</p>
            <Progress value={metrics.openRate} className="h-2 mt-2" />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{metrics.clickRate}%</div>
            <p className="text-sm text-muted-foreground">Ποσοστό Κλικ</p>
            <Progress value={metrics.clickRate} className="h-2 mt-2" />
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Επισκόπηση</TabsTrigger>
          <TabsTrigger value="list">Λίστα</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Ανάλυση Αλληλεπιδράσεων</CardTitle>
              <CardDescription>
                Κατανομή ενεργειών από τα αποσταλμένα emails
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                {metrics.eventCounts.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={metrics.eventCounts}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {metrics.eventCounts.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`${value} ενέργειες`, '']}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">Δεν υπάρχουν δεδομένα</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="list">
          <Card>
            <CardHeader>
              <CardTitle>Ιστορικό Emails</CardTitle>
              <CardDescription>
                Λεπτομερές ιστορικό αποστολών και αλληλεπιδράσεων
              </CardDescription>
            </CardHeader>
            <CardContent>
              {emailLogs.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ημερομηνία</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Ενέργεια</TableHead>
                      <TableHead>TrackingID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailLogs.map((log, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: el })}
                        </TableCell>
                        <TableCell>{log.email || "Δεν καταγράφηκε"}</TableCell>
                        <TableCell>
                          {log.event === "sent" && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Απεστάλη
                            </span>
                          )}
                          {log.event === "open" && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Αναγνώστηκε
                            </span>
                          )}
                          {log.event.startsWith("click-") && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {log.event.includes("view") ? "Κλικ: Προβολή" :
                               log.event.includes("status-pending") ? "Κλικ: Σε εκκρεμότητα" :
                               log.event.includes("status-completed") ? "Κλικ: Ολοκληρώθηκε" :
                               log.event.includes("status-info") ? "Κλικ: Πληροφορίες" :
                               "Κλικ: " + log.event.replace("click-", "")}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-gray-500">
                          {log.trackingId ? `${log.trackingId.substring(0, 8)}...` : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Δεν υπάρχουν καταγεγραμμένα emails</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PartnerEmailMetrics;
